import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type ApiError, type FindDuplicatesResult } from '@whinerp/client';
import { Button, Field, InlineAction, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * Creating a contact, with the duplicate warning beside the field that caused
 * it (F-017).
 *
 * WARN, DO NOT BLOCK. The save button never disables and no refusal is added:
 * blocking on a false positive is worse than the duplicate, because a rep who
 * cannot save the contact in front of a customer puts it in a spreadsheet and
 * then the workspace has a duplicate nobody can see at all.
 *
 * The lookup runs when a field is finished, not on every keystroke. A request
 * per character would be a request per character on a workspace's whole
 * contact table.
 */
export function NewContact({ onDone }: { onDone: () => void }): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  /** What has been typed AND left, which is what the lookup runs on. */
  const [settled, setSettled] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    // Nothing typed yet is not a query.
    if (settled.name === '' && settled.email === '' && settled.phone === '') return;
  }, [settled]);

  const duplicates = useQuery<FindDuplicatesResult, ApiError>({
    queryKey: ['duplicates', 'contact', settled],
    queryFn: () =>
      api.findDuplicates({
        path: { objectKey: 'contact' },
        body: {
          name: settled.name || null,
          email: settled.email || null,
          phone: settled.phone || null,
        },
      }),
    enabled: settled.name !== '' || settled.email !== '' || settled.phone !== '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.createContact({
        body: {
          firstName,
          lastName,
          email: email || null,
          phone: phone || null,
        },
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      onDone();
      navigate(`/contacts/${result.id}`);
    },
  });

  const matches = duplicates.data?.matches ?? [];

  return (
    <Panel data-region="new-contact">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('contact.new')}</SectionLabel>

        <div className="flex flex-wrap gap-2">
          <Field
            label={t('contact.new.first')}
            data-new-first
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
            onBlur={() => setSettled((s) => ({ ...s, name: `${firstName} ${lastName}`.trim() }))}
          />
          <Field
            label={t('contact.new.last')}
            data-new-last
            value={lastName}
            onChange={(e) => setLastName(e.currentTarget.value)}
            onBlur={() => setSettled((s) => ({ ...s, name: `${firstName} ${lastName}`.trim() }))}
          />
        </div>

        <Field
          label={t('contact.new.email')}
          data-new-email
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          onBlur={() => setSettled((s) => ({ ...s, email }))}
        />
        <Field
          label={t('contact.new.phone')}
          data-new-phone
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          onBlur={() => setSettled((s) => ({ ...s, phone }))}
        />

        {/*
          Beneath the fields that triggered it, never in front of them. A
          warning that covers the form is a warning people learn to dismiss.
        */}
        {matches.length > 0 ? (
          <div data-duplicate-warning className="flex flex-col gap-2 rounded-card border border-rule bg-warning-tint p-3">
            <p className="text-dense font-medium text-ink">{t('dup.title')}</p>
            <ul className="flex flex-col gap-2">
              {matches.map((match) => (
                <li key={match.recordId} data-duplicate={match.recordId} className="flex flex-wrap items-center gap-2">
                  <span className="text-dense text-ink">{match.label}</span>
                  {match.detail ? <span className="text-meta text-ink-meta">{match.detail}</span> : null}
                  {/* Which thing matched, not just "similar". */}
                  <span data-duplicate-reason={match.reason} className="rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600">
                    {t(`dup.reason.${match.reason}` as never)}
                  </span>
                  <span data-duplicate-confidence={match.confidence} className="text-meta text-ink-meta">
                    {t(`dup.confidence.${match.confidence}` as never)}
                  </span>
                  <InlineAction
                    data-duplicate-open={match.recordId}
                    onClick={() => {
                      onDone();
                      navigate(`/contacts/${match.recordId}`);
                    }}
                  >
                    {t('dup.useInstead')}
                  </InlineAction>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          {/*
            Never disabled by a duplicate warning. The whole rule is warn, do
            not block.
          */}
          <Button
            intent="primary"
            size="small"
            data-new-save
            busy={create.isPending}
            disabled={firstName.trim() === '' && lastName.trim() === ''}
            onClick={() => create.mutate()}
          >
            {matches.length > 0 ? t('dup.saveAnyway') : t('contact.new.save')}
          </Button>
          <Button intent="ghost" size="small" data-new-cancel onClick={onDone}>
            {t('action.cancel')}
          </Button>
        </div>
        {create.isError ? (
          <p data-new-refusal className="text-dense text-danger-text">
            {errorMessage(create.error)}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
